import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { Card, Col, Descriptions, Empty, Flex, Grid, Row, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationListOut,
  PublicationProgressionDetailOut,
} from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDateTime } from '@/utils/format';
import { ProgressionPositionPanel } from './ProgressionPositionPanel';

type Props = {
  record: PublicationProgressionDetailOut;
  publication?: PublicationListOut;
};

export const ProgressionGeneralTab = ({ record, publication }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
    {
      key: 'account_id',
      label: t('progress.progressions.fields.account_id'),
      children: orDash(record.account_id),
    },
    {
      key: 'profile_id',
      label: t('progress.progressions.fields.profile_id'),
      children: orDash(
        record.profile_id != null && (
          <ProfileIconLink profileId={record.profile_id} />
        )
      ),
    },
    {
      key: 'publication_id',
      label: t('progress.progressions.fields.publication_id'),
      children: orDash(
        record.publication_id != null && (
          <PublicationInfoPopover
            publication={publication}
            publicationId={record.publication_id}
          />
        )
      ),
    },
  ];

  const timeItems: DescriptionsProps['items'] = [
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  const positionTabs = [
    {
      key: 'audio',
      labelKey: 'progress.progressions.sections.audio_position',
      position: record.audio_position,
    },
    {
      key: 'ebook',
      labelKey: 'progress.progressions.sections.ebook_position',
      position: record.ebook_position,
    },
  ].filter((tab) => tab.position != null);

  const [activeTabKey, setActivePosition] = useState(positionTabs[0]?.key);
  const activePosition = positionTabs.find(
    (tab) => tab.key === activeTabKey
  )?.position;

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={basicItems}
            />
          </Card>
          <Card title={t('base.sections.system_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={timeItems}
            />
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        {/* One card, a tab per format the profile actually opened. */}
        <Card
          tabList={positionTabs.map(({ key, labelKey }) => ({
            key,
            label: t(labelKey),
          }))}
          activeTabKey={activeTabKey}
          onTabChange={setActivePosition}
        >
          {activePosition ? (
            <ProgressionPositionPanel position={activePosition} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </Col>
    </Row>
  );
};
